import { FC, useMemo, useRef } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { AGENTIC_AI_THINKING_LABEL } from "./constants";
import type { DummyMessage } from "./dummyConversation";

type Props = {
  startedAtLabel: string;
  messages: DummyMessage[];
};

const AgenticAIConversation: FC<Props> = ({ startedAtLabel, messages }) => {
  const scrollContainerRef = useRef<HTMLElement>(null);

  const rows = useMemo(
    () => [
      { id: "started-at", type: "started-at" as const, label: startedAtLabel },
      ...messages.map((message) => ({
        id: message.id,
        type: "message" as const,
        message
      }))
    ],
    [messages, startedAtLabel]
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: (index) => (index === 0 ? 20 : 72),
    getItemKey: (index) => rows[index].id,
    measureElement: (element) => element.getBoundingClientRect().height,
    overscan: 5
  });

  return (
    <section
      ref={scrollContainerRef}
      aria-label="Conversation"
      className="h-full min-h-0 overflow-y-auto"
    >
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          position: "relative"
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];

          return (
            <div
              data-index={virtualRow.index}
              key={row.id}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                transform: `translateY(${virtualRow.start}px)`,
                width: "100%"
              }}
              className={
                virtualRow.index === 0
                  ? undefined
                  : virtualRow.index === 1
                    ? "pt-6 pb-6"
                    : "pb-6"
              }
            >
              {row.type === "started-at" ? (
                <p className="text-xs text-txtBody">{row.label}</p>
              ) : row.message.role === "user" ? (
                <p className="rounded-lg bg-teal-50 px-4 py-3 text-sm leading-relaxed text-teal-700">
                  {row.message.content}
                </p>
              ) : (
                <div className="flex flex-col gap-1">
                  {row.message.isThinking && (
                    <p className="text-sm font-bold text-txtHeading">
                      {AGENTIC_AI_THINKING_LABEL}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed text-txtBody">
                    {row.message.content}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AgenticAIConversation;
